import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowPathIcon,
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
  ListBulletIcon,
  QueueListIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { testCaseService, TestCase } from '../services/testCaseService';
import toast from 'react-hot-toast';

interface TestStep {
  action: string;
  expectedResult: string;
}

export const EditTestCasePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Form Fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState('Functional');
    const [status, setStatus] = useState('Active');
    const [priority, setPriority] = useState('Medium');
    const [preConditions, setPreConditions] = useState<string[]>(['']);
    const [steps, setSteps] = useState<TestStep[]>([{ action: '', expectedResult: '' }]);
    const [postConditions, setPostConditions] = useState<string[]>(['']);
    
    useEffect(() => {
        if (id) {
            loadTestCase();
        }
    }, [id]);
    
    const loadTestCase = async () => {
        try {
            setLoading(true);
            const response = await testCaseService.getTestCase(id!);
            const testCase = response.data;
            
            setTitle(testCase.title || '');
            setDescription(testCase.description || '');
            setType(testCase.type || 'Functional');
            setStatus(testCase.status || 'Active');
            setPriority(testCase.priority || 'Medium');
            setPreConditions(testCase.preConditions && testCase.preConditions.length > 0 ? testCase.preConditions : ['']);
            setSteps(testCase.steps && testCase.steps.length > 0 ? testCase.steps : [{ action: '', expectedResult: '' }]);
            setPostConditions(testCase.postConditions && testCase.postConditions.length > 0 ? testCase.postConditions : ['']);
        } catch (err: any) {
            toast.error('Failed to load test case');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSave = async () => {
        if (!title.trim()) {
            toast.error('Title is required');
            return;
        }
        
        if (steps.some(step => !step.action.trim() || !step.expectedResult.trim())) {
            toast.error('All test steps must have both action and expected result');
            return;
        }
        
        try {
            setSaving(true);
            
            const payload = {
                title: title.trim(),
                description: description.trim() || null,
                type,
                status,
                priority,
                preConditions: preConditions.filter(pc => pc.trim()),
                steps: steps.filter(step => step.action.trim() && step.expectedResult.trim()),
                postConditions: postConditions.filter(pc => pc.trim())
            };
            
            await testCaseService.updateTestCase(id!, payload);
            toast.success('Test case updated successfully');
            navigate(-1);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to update test case');
        } finally {
            setSaving(false);
        }
    };
    
    // Pre-conditions handlers
    const addPreCondition = () => {
        setPreConditions([...preConditions, '']);
    };
    
    const updatePreCondition = (index: number, value: string) => {
        const updated = [...preConditions];
        updated[index] = value;
        setPreConditions(updated);
    };
    
    const removePreCondition = (index: number) => {
        if (preConditions.length === 1) {
            setPreConditions(['']);
        } else {
            setPreConditions(preConditions.filter((_, i) => i !== index));
        }
    };
    
    // Test steps handlers
    const addStep = () => {
        setSteps([...steps, { action: '', expectedResult: '' }]);
    };
    
    const updateStep = (index: number, field: 'action' | 'expectedResult', value: string) => {
        const updated = [...steps];
        updated[index][field] = value;
        setSteps(updated);
    };
    
    const removeStep = (index: number) => {
        if (steps.length === 1) {
            setSteps([{ action: '', expectedResult: '' }]);
        } else {
            setSteps(steps.filter((_, i) => i !== index));
        }
    };
    
    // Post-conditions handlers
    const addPostCondition = () => {
        setPostConditions([...postConditions, '']);
    };
    
    const updatePostCondition = (index: number, value: string) => {
        const updated = [...postConditions];
        updated[index] = value;
        setPostConditions(updated);
    };
    
    const removePostCondition = (index: number) => {
        if (postConditions.length === 1) {
            setPostConditions(['']);
        } else {
            setPostConditions(postConditions.filter((_, i) => i !== index));
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-app-bg">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-orange-500" />
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-app-bg p-6">
            <div className="max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-white rounded-full transition-colors group"
                        >
                            <ChevronLeftIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500" />
                        </button>
                        <h1 className="text-3xl font-bold font-heading text-gray-900 tracking-tight">Edit Test Case</h1>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            variant="default"
                            className="rounded-xl shadow-lg shadow-orange-500/20"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>Save</>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            className="bg-white border-gray-100 text-gray-600 hover:bg-gray-50 rounded-xl"
                            onClick={() => navigate(-1)}
                        >
                            <XMarkIcon className="h-4 w-4 mr-1.5" />
                            Cancel
                        </Button>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* 1. Test Case Details */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <InformationCircleIcon className="h-5 w-5 text-orange-500" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Test Case Details</h2>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 block">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter test case title"
                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base font-medium text-gray-900"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 block">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter test case description"
                                    rows={4}
                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base resize-none text-gray-900"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 block">Type <span className="text-red-500">*</span></label>
                                    <select
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base font-medium text-gray-900"
                                    >
                                        <option value="Functional">Functional</option>
                                        <option value="Non-Functional">Non-Functional</option>
                                        <option value="UI">UI</option>
                                        <option value="Integration">Integration</option>
                                        <option value="Performance">Performance</option>
                                        <option value="Security">Security</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 block">Status <span className="text-red-500">*</span></label>
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base font-medium text-gray-900"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Passed">Passed</option>
                                        <option value="Failed">Failed</option>
                                        <option value="Blocked">Blocked</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 block">Priority <span className="text-red-500">*</span></label>
                                    <select
                                        value={priority}
                                        onChange={(e) => setPriority(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base font-medium text-gray-900"
                                    >
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. Pre-conditions */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-2">
                                <ListBulletIcon className="h-5 w-5 text-orange-500" />
                                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Pre-conditions</h2>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addPreCondition}
                                className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 rounded-lg"
                            >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                Add Pre-condition
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {preConditions.map((condition, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0 mt-1">
                                        {idx + 1}
                                    </div>
                                    <input
                                        type="text"
                                        value={condition}
                                        onChange={(e) => updatePreCondition(idx, e.target.value)}
                                        placeholder={`Enter pre-condition ${idx + 1}`}
                                        className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base text-gray-900"
                                    />
                                    <button
                                        onClick={() => removePreCondition(idx)}
                                        className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors flex-shrink-0"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 3. Test Steps */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-2">
                                <QueueListIcon className="h-5 w-5 text-orange-500" />
                                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Test Steps <span className="text-red-500">*</span></h2>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addStep}
                                className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 rounded-lg"
                            >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                Add Step
                            </Button>
                        </div>
                        <div className="space-y-4">
                            {steps.map((step, idx) => (
                                <div key={idx} className="bg-gray-50/30 rounded-2xl border border-gray-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-black text-orange-500 uppercase tracking-widest bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200">
                                                Step {idx + 1}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => removeStep(idx)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                Action <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                value={step.action}
                                                onChange={(e) => updateStep(idx, 'action', e.target.value)}
                                                placeholder="Describe the action to perform"
                                                rows={3}
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm resize-none text-gray-900"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                                Expected Result <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                value={step.expectedResult}
                                                onChange={(e) => updateStep(idx, 'expectedResult', e.target.value)}
                                                placeholder="Describe the expected outcome"
                                                rows={3}
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm resize-none text-gray-900"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 4. Post-conditions */}
                    <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-2">
                                <CheckCircleIcon className="h-5 w-5 text-orange-500" />
                                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Post-conditions</h2>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addPostCondition}
                                className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 rounded-lg"
                            >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                Add Post-condition
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {postConditions.map((condition, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0 mt-1">
                                        {idx + 1}
                                    </div>
                                    <input
                                        type="text"
                                        value={condition}
                                        onChange={(e) => updatePostCondition(idx, e.target.value)}
                                        placeholder={`Enter post-condition ${idx + 1}`}
                                        className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base text-gray-900"
                                    />
                                    <button
                                        onClick={() => removePostCondition(idx)}
                                        className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors flex-shrink-0"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
